import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { submitSupportTicket } from "../api/supportTickets";
import SupportTicketModal from "./SupportTicketModal";

const CONTACT_SALE_SUBJECT = "Business Pro Plan - Contact Sale";

const ContactSaleSchema = Yup.object().shape({
  expectedEnvelopes: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : Number(originalValue)
    )
    .min(1, "Please enter at least 1")
    .required("Expected envelopes per month is required"),
  teamMembers: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : Number(originalValue)
    )
    .min(1, "Please enter at least 1")
    .required("Number of team members is required"),
  additionalRequirements: Yup.string().max(
    1000,
    "Additional requirements must be less than 1000 characters"
  ),
});

const ContactSaleModal = ({ isOpen, onClose, plan, user }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [ticketData, setTicketData] = useState(null);

  const initialValues = {
    expectedEnvelopes: "",
    teamMembers: user?.company?.memberCount ?? "",
    additionalRequirements: "",
  };

  const handleSubmit = async (values) => {
    setError("");
    setIsSubmitting(true);

    try {
      // Build message from form data (Heading + form data as Message)
      const messageLines = [
        "Business Pro Plan - Required Usage Information",
        "",
        `Estimated Sign Requests per Month: ${values.expectedEnvelopes}`,
        `Number of Team Members: ${values.teamMembers}`,
      ];

      if (values.additionalRequirements?.trim()) {
        messageLines.push("");
        messageLines.push("Additional Requirements:");
        messageLines.push(values.additionalRequirements.trim());
      }

      const message = messageLines.join("\n");

      const payload = {
        name: user?.name || user?.firstName || user?.email || "N/A",
        email: user?.email || "",
        company: user?.company?.name || "",
        phone: user?.company?.phone || user?.phone || "",
        subject: CONTACT_SALE_SUBJECT,
        message,
        website: "",
      };

      const response = await submitSupportTicket(payload);

      setTicketData({
        ticketId: response.ticketId,
        name: payload.name,
        email: payload.email,
        company: payload.company,
        subject: payload.subject,
      });

      setShowSuccessModal(true);
      toast.success("Your request has been submitted successfully! Our sales team will get back to you shortly.");
      onClose();
    } catch (err) {
      setError(err.message || "Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setTicketData(null);
    onClose();
  };

  return (
    <>
      {isOpen && (
        <AnimatePresence>
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-25 transition-opacity"
              onClick={onClose}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full max-w-lg"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-6 h-6 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        Contact Sale - {plan?.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-0.5">
                        Our sales team will get back to you shortly
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

              <div>
                <p className="text-gray-600 mb-6">
                  Tell us about your required usage for the Business Pro plan.
                </p>

                <Formik
                  initialValues={initialValues}
                  validationSchema={ContactSaleSchema}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting: formikSubmitting, errors, touched }) => (
                    <Form className="space-y-5">
                      <div>
                        <label
                          htmlFor="expectedEnvelopes"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Expected Sign Requests per Month *
                        </label>
                        <Field
                          type="number"
                          id="expectedEnvelopes"
                          name="expectedEnvelopes"
                          min="1"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                            errors.expectedEnvelopes && touched.expectedEnvelopes
                              ? "border-red-300 focus:ring-red-500"
                              : "border-gray-300"
                          }`}
                          placeholder="e.g., 500"
                        />
                        <ErrorMessage
                          name="expectedEnvelopes"
                          component="div"
                          className="mt-1 text-sm text-red-600"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="teamMembers"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Number of Team Members *
                        </label>
                        <Field
                          type="number"
                          id="teamMembers"
                          name="teamMembers"
                          min="1"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors ${
                            errors.teamMembers && touched.teamMembers
                              ? "border-red-300 focus:ring-red-500"
                              : "border-gray-300"
                          }`}
                          placeholder="e.g., 10"
                        />
                        <ErrorMessage
                          name="teamMembers"
                          component="div"
                          className="mt-1 text-sm text-red-600"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="additionalRequirements"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Additional Requirements
                        </label>
                        <Field
                          as="textarea"
                          id="additionalRequirements"
                          name="additionalRequirements"
                          rows={4}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors resize-none ${
                            errors.additionalRequirements &&
                            touched.additionalRequirements
                              ? "border-red-300 focus:ring-red-500"
                              : "border-gray-300"
                          }`}
                          placeholder="Tell us about any specific needs, integrations, or custom requirements..."
                        />
                        <ErrorMessage
                          name="additionalRequirements"
                          component="div"
                          className="mt-1 text-sm text-red-600"
                        />
                      </div>

                      {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      )}

                      <div className="flex gap-3 pt-2">
                        <button
                          type="button"
                          onClick={onClose}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting || formikSubmitting}
                          className="flex-1 px-4 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting || formikSubmitting
                            ? "Submitting..."
                            : "Submit Request"}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
              </div>
            </motion.div>
          </div>
        </div>
      </AnimatePresence>
      )}

      <SupportTicketModal
        isOpen={showSuccessModal}
        onClose={handleCloseSuccessModal}
        ticketData={ticketData}
      />
    </>
  );
};

export default ContactSaleModal;
